import type { FC } from 'react';
import type { RouteObject } from 'react-router';

import { lazy } from 'react';
import { Navigate } from 'react-router';
import { useRoutes } from 'react-router-dom';

import LayoutPage from '../pages/layout';

import WrapperRouteComponent from './config';
import VehicleTabs from '../features/TT_ELD_Admin/User/Vehicles/VehicleData/VehicleEdit/VehicleTabs';
import ELD from '../features/TT_ELD_Admin/User/ELD Models/ELD';
import ELDEdit from '../features/TT_ELD_Admin/User/ELD Models/ELDEdit';
import DisputedCompanies from '../features/TT_ELD_Admin/User/Companies/DisputedCompanies';
import UnpaidCompany from '../features/TT_ELD_Admin/User/Companies/UnpaidCompany';
import Bluetooth from '../features/TT_ELD_Admin/Mobile/Bluetooth/Bluetooth';
import { isWhiteLabelAdmin, WHITE_LABEL_HIDDEN_CODES } from '../../Utils/whiteLabel';
import LegacyRedirect from './LegacyRedirect';

// RTE Logs and Delta ELD share the same extra access (see RTE_ACCESS_PROJECTS)
const isRteProject = (() => {
  const forcedProject = (process.env.REACT_APP_PROJECT_NAME || '').toLowerCase();
  if (forcedProject === 'rtelogs' || forcedProject === 'deltaeld') return true;
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname.toLowerCase();
    return hostname.includes('rte') || hostname.includes('delta-eld');
  }
  return false;
})();

const unpaidInvoicesRoles = isRteProject
  ? ['sales', 'manager', 'support', 'support_manager', 'developer']
  : ['sales', 'manager'];

const Login = lazy(() => import('../features/Auth/Login'));

const NotFound = lazy(() => import('../pages/404'));
const NewAccounts = lazy(() => import('../features/TT_ELD_Admin/NewAccounts/NewAccounts'));
const MainSearchPage = lazy(() => import('../GlobalQuickSearch/MainSearchPage'));
const GlobalSearchPage = lazy(() => import('../GlobalQuickSearch/GlobalSearchPage'));
const FMCSA = lazy(() => import('../features/TT_ELD_Admin/FMCSA/FMCSA'));
const UserCompanies = lazy(() => import('../features/TT_ELD_Admin/UserCompanies/UserCompanies'));
const UserCompaniesEdit = lazy(
  () => import('../features/TT_ELD_Admin/UserCompanies/UserCompaniesEdit/UserCompaniesEdit'),
);
const Contacts = lazy(() => import('../features/TT_ELD_Admin/Contact/Contact'));
const Camera = lazy(() => import('../features/TT_ELD_Admin/Cameras/Cameras/Cameras'));
const CameraCommands = lazy(
  () => import('../features/TT_ELD_Admin/Cameras/CameraCommands/CameraCommands'),
);

const LogsEdit = lazy(() => import('../features/TT_ELD_Admin/User/Users/Logs/Log/LogEdit'));
const Users = lazy(() => import('../features/TT_ELD_Admin/User/Users/Users'));
const Dispatcher = lazy(() => import('../features/TT_ELD_Admin/User/Users/Dispatcher'));
const Admins = lazy(() => import('../features/TT_ELD_Admin/User/Admin/Admin'));
const AdminsEdit = lazy(() => import('../features/TT_ELD_Admin/User/Admin/AdminEdit'));
const User = lazy(() => import('../features/TT_ELD_Admin/User/Users/User/User'));
const FindUserByUuid = lazy(() => import('../features/TT_ELD_Admin/User/Users/FindByUuid'));
const LogEdit = lazy(() => import('../features/TT_ELD_Admin/User/Users/Logs/Log/LogEdit'));
const Supports = lazy(() => import('../features/TT_ELD_Admin/User/Support/Supports'));
const Companies = lazy(() => import('../features/TT_ELD_Admin/User/Companies/Companies'));
const FreeTrialedCompanies = lazy(
  () => import('../features/TT_ELD_Admin/User/Companies/FreeTrialedCompanies'),
);

const Company = lazy(() => import('../features/TT_ELD_Admin/User/Companies/Company/Company'));
const Owners = lazy(() => import('../features/TT_ELD_Admin/User/Owner/Owners'));
const Vehicles = lazy(() => import('../features/TT_ELD_Admin/User/Vehicles/VehiclesCompany'));
const VehicleEdit = lazy(
  () => import('../features/TT_ELD_Admin/User/Vehicles/VehicleData/VehicleEdit/VehicleEdit'),
);
const Makes = lazy(() => import('../features/TT_ELD_Admin/User/Make - Model/Make/Make'));
const MakeEdit = lazy(() => import('../features/TT_ELD_Admin/User/Make - Model/Make/MakeEdit'));
const Models = lazy(() => import('../features/TT_ELD_Admin/User/Make - Model/Model/Model'));
const ModelEdit = lazy(() => import('../features/TT_ELD_Admin/User/Make - Model/Model/ModelEdit'));
const CheckTruck = lazy(
  () => import('../features/TT_ELD_Admin/User/Make - Model/CheckTruck/CheckTruck'),
);
const Documents = lazy(() => import('../features/TT_ELD_Admin/User/Users/User/Documents'));
const LogBook = lazy(() => import('../features/TT_ELD_Admin/User/LogBook/LogBook'));
const LogBookFind = lazy(() => import('../features/TT_ELD_Admin/User/LogBook/LogBookEdit'));
const CompanyErrors = lazy(
  () => import('../features/TT_ELD_Admin/CompanyErrors/CompanyErrors/CompanyErrors'),
);
const CompanyErrorMain = lazy(
  () => import('../features/TT_ELD_Admin/CompanyErrors/CompanyErrors/SupportHistory/SupportMain'),
);
const TopErrors = lazy(
  () => import('../features/TT_ELD_Admin/CompanyErrors/TopErrorsCompany/TopErrors'),
);
const Histories = lazy(() => import('../features/TT_ELD_Admin/Team/Histories/Histories'));
const ServerConfiguration = lazy(
  () => import('../features/TT_ELD_Admin/Team/ServerConfiguration/ServerConfiguration'),
);
const DevicesEld = lazy(() => import('../features/TT_ELD_Admin/Mobile/DevicesEld/DevicesEld'));
const Malfunction = lazy(() => import('../features/TT_ELD_Admin/Mobile/Malfunction/Malfunction'));
const System = lazy(() => import('../features/TT_ELD_Admin/Team/System/System'));
const CheckStripe = lazy(() => import('../features/TT_ELD_Admin/Team/StripeCheck/CheckTabs'));
const Builds = lazy(() => import('../features/TT_ELD_Admin/Mobile/Builds/Builds'));
const Firmware = lazy(() => import('../features/TT_ELD_Admin/Mobile/Firmware/Firmware'));
const BulkConfig = lazy(() => import('../features/TT_ELD_Admin/Mobile/BulkConfig/BulkConfig'));
const EldKeys = lazy(() => import('../features/TT_ELD_Admin/Mobile/EldKeys/EldKeys'));
const FirmwareForceUpdate = lazy(
  () => import('../features/TT_ELD_Admin/Mobile/FirmwareForceUpdate/FirmwareForceUpdate'),
);
const Provider = lazy(() => import('../features/TT_ELD_Admin/Team/Provider/Provider'));
const ProviderEdit = lazy(
  () => import('../features/TT_ELD_Admin/Team/Provider/ProviderTable/ProviderEdit'),
);
const ProviderActivity = lazy(
  () => import('../features/TT_ELD_Admin/Team/ProviderActivity/ProviderActivity'),
);
const ProviderRequest = lazy(
  () => import('../features/TT_ELD_Admin/Team/ProviderRequest/ProviderRequest'),
);
const LoginHistory = lazy(
  () => import('../features/TT_ELD_Admin/Team/LoginHistory/LoginHistory'),
);
const WeighStation = lazy(() => import('../features/TT_ELD_Admin/Team/WeighStation/WeighStation'));

const Contracts = lazy(() => import('../features/TT_ELD_Admin/Contracts/Contracts'));
const ContractsEdit = lazy(
  () => import('../features/TT_ELD_Admin/Contracts/Contract_Table/ContractEdit'),
);
const ContractCreate = lazy(
  () => import('../features/TT_ELD_Admin/Contracts/Contract_Table/CreateContractForm'),
);
const LinkedCompanies = lazy(
  () => import('../features/TT_ELD_Admin/Contracts/LinkedCompanies/LinkedCompanies'),
);

const CameraSIMCards = lazy(() => import('../features/TT_ELD_Admin/Cameras/SIMCards/SIMCards'));
const Alerts = lazy(() => import('../features/TT_ELD_Admin/Cameras/Alerts/Alerts'));
const DashcamRequest = lazy(() => import('../features/TT_ELD_Admin/DashcamRequest/DashcamRequest'));
const CreateDashcamRequest = lazy(
  () => import('../features/TT_ELD_Admin/DashcamRequest/CreateDashcamRequest'),
);
const EditDashcamRequest = lazy(
  () => import('../features/TT_ELD_Admin/DashcamRequest/DashcamRequestEdit'),
);

const DashcamContract = lazy(
  () => import('../features/TT_ELD_Admin/DashcamContract/DashcamContract'),
);
const CreateDashcamContract = lazy(
  () => import('../features/TT_ELD_Admin/DashcamContract/CreateDashcamContract'),
);
const EditDashcamContract = lazy(
  () => import('../features/TT_ELD_Admin/DashcamContract/DashcamContractEdit'),
);

const routeList: RouteObject[] = [
  { path: '/login', element: <WrapperRouteComponent element={<Login />} titleId="title.login" /> },
  {
    path: '/',
    element: <WrapperRouteComponent element={<LayoutPage />} titleId="" />,
    children: [
      { path: '', element: <Navigate to="search" /> },
      {
        path: 'account',
        element: (
          <WrapperRouteComponent
            element={<NewAccounts />}
            titleId="title.newAccounts"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager', 'marketolog']}
          />
        ),
      },
      {
        path: 'search',
        element: <WrapperRouteComponent element={<MainSearchPage />} titleId="" />,
      },
      {
        path: 'global-search',
        element: <WrapperRouteComponent element={<GlobalSearchPage />} titleId="" />,
      },
      {
        path: 'contact',
        element: (
          <WrapperRouteComponent
            element={<Contacts />}
            titleId="title.contacts"
            roles={['sales', 'manager', 'marketolog']}
          />
        ),
      },
      {
        path: 'fmcsa',
        element: (
          <WrapperRouteComponent
            element={<FMCSA />}
            titleId="title.fmcsa"
            roles={['support', 'developer', 'manager', 'support_manager']}
          />
        ),
      },
      {
        path: 'camera/cameras',
        element: (
          <WrapperRouteComponent
            element={<Camera />}
            titleId="title.cameras"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'camera/camera-commands',
        element: (
          <WrapperRouteComponent
            element={<CameraCommands />}
            titleId="title.camera-commands"
            roles={['developer', 'manager']}
          />
        ),
      },
      {
        path: 'sales/disabled-companies',
        element: (
          <WrapperRouteComponent
            element={<UnpaidCompany />}
            titleId="title.disabledCompany"
            roles={unpaidInvoicesRoles}
          />
        ),
      },
      {
        path: 'sales/disabled-companies/:id',
        element: (
          <WrapperRouteComponent
            element={<Company />}
            titleId="title.companies"
            roles={unpaidInvoicesRoles}
          />
        ),
      },
      {
        path: 'sales/disabled-companies/:id/:id',
        element: (
          <WrapperRouteComponent
            element={<VehicleEdit vehicleId="" />}
            titleId="title.companies"
            roles={unpaidInvoicesRoles}
          />
        ),
      },
      {
        path: 'sales/disputed-companies',
        element: (
          <WrapperRouteComponent
            element={<DisputedCompanies />}
            titleId="title.disabledCompany"
            roles={['manager', 'sales', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'sales/disputed-companies/:id',
        element: (
          <WrapperRouteComponent
            element={<Company />}
            titleId="title.companies"
            roles={['manager', 'sales', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'sales/disputed-companies/:id/:id',
        element: (
          <WrapperRouteComponent
            element={<VehicleEdit vehicleId="" />}
            titleId="title.companies"
            roles={['manager', 'sales', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'sales/contracts',
        element: (
          <WrapperRouteComponent
            element={<Contracts />}
            titleId="title.contracts"
            roles={['sales', 'manager']}
          />
        ),
      },
      {
        path: '/sales/linked-companies',
        element: (
          <WrapperRouteComponent
            element={<LinkedCompanies />}
            titleId="title.linked-companies"
            roles={['sales', 'manager']}
          />
        ),
      },
      {
        path: 'sales/contracts/:id/edit',
        element: (
          <WrapperRouteComponent
            element={<ContractsEdit />}
            titleId="title.contracts.edit"
            roles={['sales', 'manager']}
          />
        ),
      },
      {
        path: 'sales/contracts/create',
        element: (
          <WrapperRouteComponent
            element={<ContractCreate />}
            titleId="title.contracts.create"
            roles={['sales', 'manager']}
          />
        ),
      },

      {
        path: 'logs/log/:logId/:id',
        element: (
          <WrapperRouteComponent
            element={<LogsEdit />}
            titleId="title.logsEdit"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'logs/logzeros/:logId/:id',
        element: (
          <WrapperRouteComponent
            element={<LogsEdit />}
            titleId="title.logsEdit"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'logs/logzeros/:logId',
        element: (
          <WrapperRouteComponent
            element={<LogsEdit />}
            titleId="title.logsEdit"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'user-companies',
        element: (
          <WrapperRouteComponent
            element={<UserCompanies />}
            titleId="title.userCompanies"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user-companies/:id',
        element: (
          <WrapperRouteComponent
            element={<UserCompaniesEdit />}
            titleId="title.userCompanies"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/supports',
        element: (
          <WrapperRouteComponent
            element={<Supports />}
            titleId="title.supports"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/dispatcher',
        element: (
          <WrapperRouteComponent
            element={<Dispatcher />}
            titleId="title.dispatcher"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/dispatcher/:id',
        element: (
          <WrapperRouteComponent
            element={<User />}
            titleId="title.dispatcher"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/admins',
        element: (
          <WrapperRouteComponent
            element={<Admins />}
            titleId="title.users"
            roles={['sales', 'support', 'support_manager', 'developer', 'manager']}
          />
        ),
      },
      {
        path: 'user/admins/:id',
        element: (
          <WrapperRouteComponent
            element={<AdminsEdit />}
            titleId="title.users"
            roles={['sales', 'support', 'support_manager', 'developer', 'manager']}
          />
        ),
      },
      {
        path: 'user/users',
        element: (
          <WrapperRouteComponent
            element={<Users />}
            titleId="title.users"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/users/:id',
        element: (
          <WrapperRouteComponent
            element={<User />}
            titleId="title.user/:id"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/find-by-uuid',
        element: (
          <WrapperRouteComponent
            element={<FindUserByUuid />}
            titleId="title.findUserByUuid"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/users/:id/log/:logId/:id',
        element: (
          <WrapperRouteComponent
            element={<LogEdit />}
            titleId="title.logs/:id"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/users/:id/logzeros/:logId/:id',
        element: (
          <WrapperRouteComponent
            element={<LogEdit />}
            titleId="title.logs/:id"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: '/companies',
        element: <WrapperRouteComponent element={<Companies />} titleId="title.companies" />,
      },
      {
        path: '/companies/:id',
        element: <WrapperRouteComponent element={<Company />} titleId="title.companyEdit" />,
      },
      {
        path: '/companies/:id/:id',
        element: (
          <WrapperRouteComponent
            element={<VehicleTabs vehicleId="" />}
            titleId="title.companyEdit"
          />
        ),
      },
      {
        path: 'user/owners',
        element: (
          <WrapperRouteComponent
            element={<Owners />}
            titleId="title.owners"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/owners/:id',
        element: (
          <WrapperRouteComponent
            element={<UserCompanies />}
            titleId="title.owners"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'vehicles/vehicles',
        element: (
          <WrapperRouteComponent
            element={<Vehicles />}
            titleId="title.vehicles"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'vehicles/vehicles/:id',
        element: (
          <WrapperRouteComponent
            element={<VehicleTabs vehicleId="" />}
            titleId="title.vehicleEdit"
            roles={['sales', 'developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'vehicles/makes',
        element: (
          <WrapperRouteComponent
            element={<Makes />}
            titleId="title.makes"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'vehicles/makes/:id',
        element: (
          <WrapperRouteComponent
            element={<MakeEdit />}
            titleId="title.makeEdit"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'vehicles/models',
        element: (
          <WrapperRouteComponent
            element={<Models />}
            titleId="title.models"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'vehicles/models/:id',
        element: (
          <WrapperRouteComponent
            element={<ModelEdit />}
            titleId="title.modelEdit"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'vehicles/check-truck',
        element: (
          <WrapperRouteComponent
            element={<CheckTruck />}
            titleId="title.check-truck"
            roles={['developer', 'manager']}
          />
        ),
      },
      {
        path: '/documents',
        element: (
          <WrapperRouteComponent
            element={<Documents />}
            titleId="title.documents"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/eld_model',
        element: <WrapperRouteComponent element={<ELD />} titleId="title.eld" />,
      },
      {
        path: 'user/eld_model/:id',
        element: <WrapperRouteComponent element={<ELDEdit />} titleId="title.eld" />,
      },
      {
        path: 'user/logbook',
        element: (
          <WrapperRouteComponent
            element={<LogBook />}
            titleId="title.logbook"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'user/logbook/:id',
        element: (
          <WrapperRouteComponent
            element={<LogBookFind />}
            titleId="title.logbook"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'errors/company-errors',
        element: (
          <WrapperRouteComponent
            element={<CompanyErrors />}
            titleId="title.company-errors"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'errors/company-errors/:id',
        element: (
          <WrapperRouteComponent
            element={<CompanyErrorMain />}
            titleId="title.company-errors"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'errors/company-errors/:id/:id',
        element: (
          <WrapperRouteComponent
            element={<UserCompanies />}
            titleId="title.company-errors"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'errors/top-errors',
        element: (
          <WrapperRouteComponent
            element={<TopErrors />}
            titleId="title.topErrors"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'errors/top-errors/:id',
        element: (
          <WrapperRouteComponent
            element={<CompanyErrorMain />}
            titleId="title.topErrors/:id"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'errors/top-errors/:id/:id',
        element: (
          <WrapperRouteComponent
            element={<UserCompanies />}
            titleId="title.top-errors"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: '/histories',
        element: (
          <WrapperRouteComponent
            element={<Histories />}
            titleId="title.histories"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'system-config/maintenance-message',
        element: (
          <WrapperRouteComponent
            element={<ServerConfiguration />}
            titleId="title.serverConfiguration"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },

      {
        path: 'system-config/system',
        element: (
          <WrapperRouteComponent
            element={<System />}
            titleId="title.system"
            roles={['developer', 'manager']}
          />
        ),
      },
      {
        path: 'sales/check-stripe',
        element: (
          <WrapperRouteComponent
            element={<CheckStripe />}
            titleId="title.check-stripe"
            roles={['developer', 'manager', 'sales']}
          />
        ),
      },
      {
        path: 'sales/check-stripe/:id',
        element: (
          <WrapperRouteComponent
            element={<Company />}
            titleId="title.check-stripe"
            roles={['developer', 'manager', 'sales']}
          />
        ),
      },
      {
        path: 'sales/check-stripe/:id/:id',
        element: (
          <WrapperRouteComponent
            element={<VehicleEdit vehicleId="" />}
            titleId="title.check-stripe"
            roles={['developer', 'manager', 'sales']}
          />
        ),
      },
      {
        path: 'external-integrations/providers',
        element: (
          <WrapperRouteComponent
            element={<Provider />}
            titleId="title.provider"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'external-integrations/providers/:id',
        element: (
          <WrapperRouteComponent
            element={<ProviderEdit />}
            titleId="title.provider"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'external-integrations/provider-activities',
        element: (
          <WrapperRouteComponent
            element={<ProviderActivity />}
            titleId="title.provider-activity"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'external-integrations/provider-requests',
        element: (
          <WrapperRouteComponent
            element={<ProviderRequest />}
            titleId="title.provider-request"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'system-config/login-history',
        element: (
          <WrapperRouteComponent
            element={<LoginHistory />}
            titleId="title.login-history"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'weigh-station/',
        element: (
          <WrapperRouteComponent
            element={<WeighStation />}
            titleId="title.weigh-station"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'mobile/builds',
        element: (
          <WrapperRouteComponent
            element={<Builds />}
            titleId="title.builds"
            roles={['developer', 'manager']}
          />
        ),
      },
      {
        path: 'mobile/firmware',
        element: (
          <WrapperRouteComponent
            element={<Firmware />}
            titleId="title.firmware"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'system-config/eld-keys',
        element: (
          <WrapperRouteComponent element={<EldKeys />} titleId="title.eldKeys" roles={['manager']} />
        ),
      },
      {
        path: 'mobile/bulk-config',
        element: (
          <WrapperRouteComponent
            element={<BulkConfig />}
            titleId="title.bulkConfig"
            roles={['developer', 'manager']}
          />
        ),
      },
      {
        path: 'mobile/firmware-force-update',
        element: (
          <WrapperRouteComponent
            element={<FirmwareForceUpdate />}
            titleId="title.firmwareForceUpdate"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'mobile/bluetooth',
        element: (
          <WrapperRouteComponent
            element={<Bluetooth />}
            titleId="title.bluetooth"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'mobile/devices-eld',
        element: (
          <WrapperRouteComponent
            element={<DevicesEld />}
            titleId="title.devicesEld"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'mobile/devices-eld/:id',
        element: (
          <WrapperRouteComponent
            element={<DevicesEld />}
            titleId="title.devicesEld"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      {
        path: 'system-config/malfunction-letters',
        element: (
          <WrapperRouteComponent
            element={<Malfunction />}
            titleId="title.malfunction"
            roles={['developer', 'manager', 'support', 'support_manager']}
          />
        ),
      },
      // Old URLs from before the sidebar regroup redirect to the new ones (see LegacyRedirect)
      { path: 'team/*', element: <LegacyRedirect /> },
      { path: 'mobile/eld-keys', element: <LegacyRedirect /> },
      { path: 'mobile/malfunction', element: <LegacyRedirect /> },
      {
        path: '/sales/dashcam-request',
        element: (
          <WrapperRouteComponent
            element={<DashcamRequest />}
            titleId="title.dashcam-request"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'sales/free-trialed-companies',
        element: (
          <WrapperRouteComponent
            element={<FreeTrialedCompanies />}
            titleId="title.freeTrialedCompanies"
            roles={['sales', 'manager', 'support_manager']}
          />
        ),
      },
      {
        path: '/sales/dashcam-request/create',
        element: (
          <WrapperRouteComponent
            element={<CreateDashcamRequest />}
            titleId="title.dashcam-request"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: '/sales/dashcam-request/:id/edit',
        element: (
          <WrapperRouteComponent
            element={<EditDashcamRequest />}
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
            titleId="title.dashcam-request"
          />
        ),
      },
      {
        path: 'sales/dashcam-contracts',
        element: (
          <WrapperRouteComponent
            element={<DashcamContract />}
            titleId="title.dashcam-contracts"
            roles={['sales', 'manager']}
          />
        ),
      },
      {
        path: 'sales/dashcam-contracts/create',
        element: (
          <WrapperRouteComponent
            element={<CreateDashcamContract />}
            titleId="title.dashcam-contracts"
            roles={['sales', 'manager']}
          />
        ),
      },
      {
        path: 'sales/dashcam-contracts/:id/edit',
        element: (
          <WrapperRouteComponent
            element={<EditDashcamContract />}
            titleId="title.dashcam-contracts"
            roles={['sales', 'manager']}
          />
        ),
      },
      {
        path: 'camera/simcards',
        element: (
          <WrapperRouteComponent
            element={<CameraSIMCards />}
            titleId="title.simcards"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: 'camera/alerts',
        element: (
          <WrapperRouteComponent
            element={<Alerts />}
            titleId="title.alerts"
            roles={['developer', 'manager', 'support', 'support_manager', 'sales']}
          />
        ),
      },
      {
        path: '*',
        element: <WrapperRouteComponent element={<NotFound />} titleId="title.notFount" />,
      },
    ],
  },
];

const RTE_ALLOWED_WHITE_LABEL_ROUTES = ['sales/disabled-companies', 'sales/free-trialed-companies'];

const filterRoutesForWhiteLabel = (routes: RouteObject[]): RouteObject[] => {
  if (!isWhiteLabelAdmin()) return routes;

  return routes
    .filter((route) => {
      if (!route.path) return true;

      if (
        isRteProject &&
        RTE_ALLOWED_WHITE_LABEL_ROUTES.some((path) => route.path?.includes(path))
      ) {
        return true;
      }

      return !WHITE_LABEL_HIDDEN_CODES.some((code) => route.path?.includes(code));
    })
    .map((route) => {
      if (route.children) {
        return {
          ...route,
          children: filterRoutesForWhiteLabel(route.children),
        };
      }
      return route;
    });
};

const RenderRouter: FC = () => {
  const filteredRoutes = filterRoutesForWhiteLabel(routeList);
  const element = useRoutes(filteredRoutes);

  return element;
};

export default RenderRouter;
